import { requestPost } from "../util/request";

/** 获取画布图片上传预签名URL请求参数 */
export interface CanvasUploadUrlRequest {
  userId: string;
  mimeType: string;
  fileSize: number;
}

/** 画布图片上传预签名URL响应 data */
export interface CanvasUploadUrlData {
  userId: string;
  fileName: string;
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresAt: string;
  duration: number;
  callbackHeaders: Record<string, string>;
}

/** 画布图片上传预签名URL完整响应 */
export interface CanvasUploadUrlResponse {
  success: boolean;
  data: CanvasUploadUrlData;
}

/** 上传到OSS后返回的上传结果 */
export interface UploadToOSSResult {
  url: string;
  fileName: string;
  width: number;
  height: number;
  contentType: string;
  fileSize: number;
  userId: string;
  originalUrl: string;
  presignedUrl: string;
  uploadedAt: string;
}

const getChatUploadUrl = () => "/imagehub/api/image/chat-upload-url";

/**
 * 获取聊天图片上传的预签名URL（聊天框用）
 */
export const chatUploadUrl = async (
  body: CanvasUploadUrlRequest,
): Promise<CanvasUploadUrlResponse | null> => {
  return requestPost<CanvasUploadUrlResponse>(
    getChatUploadUrl(),
    body,
    true,
  );
};

const getCanvasUploadUrl = () => "/imagehub/api/image/canvas-upload-url";

/**
 * 获取画布图片上传的预签名URL（画布粘贴用）
 */
export const canvasUploadUrl = async (
  body: CanvasUploadUrlRequest,
): Promise<CanvasUploadUrlResponse | null> => {
  return requestPost<CanvasUploadUrlResponse>(
    getCanvasUploadUrl(),
    body,
    true,
  );
};

/**
 * 封装 OSS 上传流程（画布粘贴用，调用 canvas-upload-url）
 */
export async function uploadFileToOSS(
  file: File,
  userId: string,
): Promise<UploadToOSSResult> {
  const {width, height} = await getImageSize(file);

  const res = await canvasUploadUrl({
    userId,
    mimeType: file.type,
    fileSize: file.size,
  });
  if (!res?.data) {
    throw new Error('获取画布上传预签名URL失败');
  }
  const { uploadUrl, callbackHeaders, bucket, key, fileName } = res.data;

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: callbackHeaders?.["x-oss-callback"]
      ? { "x-oss-callback": callbackHeaders["x-oss-callback"] }
      : undefined,
  });

  if (!putRes.ok) {
    throw new Error(`OSS upload failed: ${putRes.status}`);
  }

  return {
    userId,
    fileName: fileName,
    originalUrl: fileName,
    presignedUrl: fileName,
    url: fileName,
    contentType: file.type,
    fileSize: file.size,
    uploadedAt: new Date().toISOString(),
    width,
    height,
  };
}

/**
 * 封装 OSS 上传流程（聊天框用，调用 chat-upload-url）
 */
/** 从 File 中提取图片宽高（兜底 canvas blob 可能返回 0 的情况） */
async function getImageSize(file: File): Promise<{width: number; height: number}> {
  try {
    const bitmap = await createImageBitmap(file);
    if (bitmap.width > 0 && bitmap.height > 0) {
      const size = {width: bitmap.width, height: bitmap.height};
      bitmap.close();
      return size;
    }
    bitmap.close();
  } catch {}
  // 降级：用 Image 加载
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({width: img.naturalWidth, height: img.naturalHeight});
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('getImageSize failed'));
    };
    img.src = url;
  });
}

export async function uploadFileToChatOSS(
  file: File,
  userId: string,
): Promise<UploadToOSSResult> {
  const {width, height} = await getImageSize(file);

  const res = await chatUploadUrl({
    userId,
    mimeType: file.type,
    fileSize: file.size,
  });
  if (!res?.data) {
    throw new Error('获取聊天上传预签名URL失败');
  }
  const { uploadUrl, callbackHeaders, bucket, key, fileName } = res.data;

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: callbackHeaders?.["x-oss-callback"]
      ? { "x-oss-callback": callbackHeaders["x-oss-callback"] }
      : undefined,
  });

  if (!putRes.ok) {
    throw new Error(`OSS upload failed: ${putRes.status}`);
  }

  return {
    userId,
    fileName: fileName,
    originalUrl: fileName,
    presignedUrl: fileName,
    url: fileName,
    contentType: file.type,
    fileSize: file.size,
    uploadedAt: new Date().toISOString(),
    width,
    height,
  };
}
